<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, fromUnixTime, isThisYear } from 'date-fns';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { jumpToChatMessage } from 'dashboard/helper/jumpToChatMessage';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

// WhatsApp-style "Media and docs" strip (owner 2026-10-06). Built only from the
// attachments page MessagesView already fetches on every chat open, so it adds
// no requests; older pages load only when someone presses "Load more".
const PREVIEW_COUNT = 4;
const VISUAL_TYPES = ['image', 'video'];
const DOC_TYPES = ['file', 'audio'];
const DOC_ICONS = { audio: 'i-lucide-file-audio', file: 'i-lucide-file-text' };

const store = useStore();
const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');
const loadedAttachments = useMapGetter('getSelectedChatAttachments');
const total = useMapGetter('getSelectedChatAttachmentsTotal');

const olderPages = ref([]);
const nextPage = ref(2);
const isLoadingMore = ref(false);
const dialogRef = ref(null);
const activeTab = ref('media');
const galleryItem = ref(null);
// Links come from message text (server-extracted), fetched only when the
// Links tab is opened; null = not loaded yet for this chat.
const links = ref(null);
const isLoadingLinks = ref(false);
const linksFailed = ref(false);

watch(
  () => currentChat.value.id,
  () => {
    olderPages.value = [];
    links.value = null;
    linksFailed.value = false;
    nextPage.value = 2;
    galleryItem.value = null;
  }
);

const allAttachments = computed(() => {
  const seen = new Set();
  return [...loadedAttachments.value, ...olderPages.value]
    .filter(item => {
      const key = item.data_url;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => b.message_id - a.message_id);
});
const visual = computed(() =>
  allAttachments.value.filter(item => VISUAL_TYPES.includes(item.file_type))
);
const docs = computed(() =>
  allAttachments.value.filter(item => DOC_TYPES.includes(item.file_type))
);
const preview = computed(() => visual.value.slice(0, PREVIEW_COUNT));
const totalCount = computed(() =>
  Math.max(total.value, allAttachments.value.length)
);
const canLoadMore = computed(() => allAttachments.value.length < total.value);

const fileName = item =>
  decodeURIComponent((item.data_url || '').split('?')[0].split('/').pop()) ||
  item.extension ||
  item.file_type;

const fileSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// created_at comes with the attachments API; files that arrived live over the
// websocket don't carry it, so those simply show no date.
const sentAt = item => {
  if (!item.created_at) return '';
  const date = fromUnixTime(item.created_at);
  return format(date, isThisYear(date) ? 'd MMM, HH:mm' : 'd MMM yyyy');
};
const sentDay = item => {
  if (!item.created_at) return '';
  const date = fromUnixTime(item.created_at);
  return format(date, isThisYear(date) ? 'd MMM' : 'MMM yyyy');
};

const loadLinks = async () => {
  if (links.value || isLoadingLinks.value) return;
  const conversationId = currentChat.value.id;
  isLoadingLinks.value = true;
  linksFailed.value = false;
  try {
    const { data } = await ConversationApi.getLinks(conversationId);
    if (conversationId === currentChat.value.id) links.value = data.payload;
  } catch {
    linksFailed.value = true;
  } finally {
    isLoadingLinks.value = false;
  }
};

const selectTab = tab => {
  activeTab.value = tab;
  if (tab === 'links') loadLinks();
};

const tabLabel = tab => {
  if (tab === 'media') {
    return t('CONVERSATION_SIDEBAR.MEDIA.TAB_MEDIA', {
      count: visual.value.length,
    });
  }
  if (tab === 'docs') {
    return t('CONVERSATION_SIDEBAR.MEDIA.TAB_DOCS', {
      count: docs.value.length,
    });
  }
  return links.value
    ? t('CONVERSATION_SIDEBAR.MEDIA.TAB_LINKS_COUNT', {
        count: links.value.length,
      })
    : t('CONVERSATION_SIDEBAR.MEDIA.TAB_LINKS');
};

const linkHost = url => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const openAll = () => {
  activeTab.value =
    visual.value.length || !docs.value.length ? 'media' : 'docs';
  dialogRef.value?.open();
};

const loadMore = async () => {
  isLoadingMore.value = true;
  try {
    const { data } = await ConversationApi.getAllAttachments(
      currentChat.value.id,
      nextPage.value
    );
    olderPages.value = [...olderPages.value, ...data.payload];
    nextPage.value += 1;
  } finally {
    isLoadingMore.value = false;
  }
};

const showInChat = item => {
  dialogRef.value?.close();
  jumpToChatMessage(store, currentChat.value, item.message_id);
};
</script>

<template>
  <div v-if="totalCount" class="px-4 pb-3">
    <button
      type="button"
      class="flex items-center w-full gap-2 py-2 text-start"
      @click="openAll"
    >
      <Icon icon="i-lucide-images" class="size-4 text-n-slate-11" />
      <span class="flex-1 text-sm font-medium text-n-slate-12">
        {{ t('CONVERSATION_SIDEBAR.MEDIA.TITLE') }}
      </span>
      <span class="text-sm text-n-slate-11">{{ totalCount }}</span>
      <Icon icon="i-lucide-chevron-right" class="size-4 text-n-slate-11" />
    </button>
    <div v-if="preview.length" class="grid grid-cols-4 gap-1.5">
      <button
        v-for="item in preview"
        :key="item.data_url"
        v-tooltip.top="sentAt(item)"
        type="button"
        class="overflow-hidden rounded-md aspect-square bg-n-alpha-2"
        @click="galleryItem = item"
      >
        <img
          v-if="item.file_type === 'image'"
          :src="item.thumb_url || item.data_url"
          class="object-cover w-full h-full"
          loading="lazy"
        />
        <Icon
          v-else
          icon="i-lucide-play"
          class="m-auto size-5 text-n-slate-11"
        />
      </button>
    </div>
    <button
      v-else
      type="button"
      class="text-xs text-n-slate-11 hover:underline"
      @click="openAll"
    >
      {{ t('CONVERSATION_SIDEBAR.MEDIA.DOCS_ONLY', { count: docs.length }) }}
    </button>

    <Dialog
      ref="dialogRef"
      :title="t('CONVERSATION_SIDEBAR.MEDIA.TITLE')"
      :show-confirm-button="false"
      :cancel-button-label="t('CONVERSATION_SIDEBAR.MEDIA.CLOSE')"
      width="2xl"
      overflow-y-auto
    >
      <div class="flex gap-2">
        <button
          v-for="tab in ['media', 'docs', 'links']"
          :key="tab"
          type="button"
          class="px-3 py-1 text-sm rounded-md"
          :class="
            activeTab === tab
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="selectTab(tab)"
        >
          {{ tabLabel(tab) }}
        </button>
      </div>
      <div class="max-h-[60vh] overflow-y-auto">
        <div
          v-if="activeTab === 'media'"
          class="grid grid-cols-4 gap-2 sm:grid-cols-5"
        >
          <button
            v-for="item in visual"
            :key="item.data_url"
            v-tooltip.top="sentAt(item)"
            type="button"
            class="relative overflow-hidden rounded-md aspect-square bg-n-alpha-2"
            @click="galleryItem = item"
          >
            <span
              v-if="item.created_at"
              class="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 text-[10px] text-white text-start bg-gradient-to-t from-modal-backdrop-dark to-transparent"
            >
              {{ sentDay(item) }}
            </span>
            <img
              v-if="item.file_type === 'image'"
              :src="item.thumb_url || item.data_url"
              class="object-cover w-full h-full"
              loading="lazy"
            />
            <Icon
              v-else
              icon="i-lucide-play"
              class="m-auto size-6 text-n-slate-11"
            />
          </button>
        </div>
        <div v-else-if="activeTab === 'docs'" class="flex flex-col">
          <div
            v-for="item in docs"
            :key="item.data_url"
            class="flex items-center gap-3 py-2 border-b border-n-weak"
          >
            <Icon
              :icon="DOC_ICONS[item.file_type]"
              class="size-5 text-n-slate-11 shrink-0"
            />
            <a
              :href="item.data_url"
              target="_blank"
              rel="noopener noreferrer"
              class="flex-1 min-w-0 text-sm truncate text-n-slate-12 hover:underline"
            >
              {{ fileName(item) }}
            </a>
            <span class="text-xs text-n-slate-11 shrink-0">
              {{ sentAt(item) }}
            </span>
            <span class="text-xs text-n-slate-11 shrink-0">
              {{ fileSize(item.file_size) }}
            </span>
            <button
              type="button"
              class="text-xs text-n-blue-11 shrink-0 hover:underline"
              @click="showInChat(item)"
            >
              {{ t('CONVERSATION_SIDEBAR.MEDIA.SHOW_IN_CHAT') }}
            </button>
          </div>
        </div>
        <div v-else class="flex flex-col">
          <p v-if="isLoadingLinks" class="py-2 mb-0 text-sm text-n-slate-11">
            {{ t('CONVERSATION_SIDEBAR.MEDIA.LINKS_LOADING') }}
          </p>
          <p v-else-if="linksFailed" class="py-2 mb-0 text-sm text-n-ruby-11">
            {{ t('CONVERSATION_SIDEBAR.MEDIA.LINKS_FAILED') }}
          </p>
          <p
            v-else-if="links && !links.length"
            class="py-2 mb-0 text-sm text-n-slate-11"
          >
            {{ t('CONVERSATION_SIDEBAR.MEDIA.LINKS_EMPTY') }}
          </p>
          <div
            v-for="item in links || []"
            :key="item.url"
            class="flex items-center gap-3 py-2 border-b border-n-weak"
          >
            <Icon
              icon="i-lucide-link"
              class="size-5 text-n-slate-11 shrink-0"
            />
            <a
              :href="item.url"
              target="_blank"
              rel="noopener noreferrer"
              class="flex flex-col flex-1 min-w-0 hover:underline"
            >
              <span class="text-sm truncate text-n-slate-12">
                {{ linkHost(item.url) }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ item.url }}
              </span>
            </a>
            <span
              v-if="item.private"
              class="px-1 text-xs rounded bg-n-amber-3 text-n-amber-11 shrink-0"
            >
              {{ t('CONVERSATION.PINNED_BAR.NOTE') }}
            </span>
            <span class="text-xs text-n-slate-11 shrink-0">
              {{ sentAt(item) }}
            </span>
            <button
              type="button"
              class="text-xs text-n-blue-11 shrink-0 hover:underline"
              @click="showInChat(item)"
            >
              {{ t('CONVERSATION_SIDEBAR.MEDIA.SHOW_IN_CHAT') }}
            </button>
          </div>
        </div>
        <button
          v-if="canLoadMore && activeTab !== 'links'"
          type="button"
          class="w-full py-2 mt-2 text-sm text-n-blue-11 hover:underline disabled:opacity-50"
          :disabled="isLoadingMore"
          @click="loadMore"
        >
          {{ t('CONVERSATION_SIDEBAR.MEDIA.LOAD_MORE') }}
        </button>
      </div>
    </Dialog>

    <GalleryView
      v-if="galleryItem"
      :show="!!galleryItem"
      :attachment="galleryItem"
      :all-attachments="visual"
      @close="galleryItem = null"
    />
  </div>
</template>
